'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FeedbackPromptProps {
  orderId: string;
  isDelivered: boolean;
}

const FeedbackPrompt = ({ orderId, isDelivered }: FeedbackPromptProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSubmit = () => {
    if (rating > 0) {
      setSubmitted(true);
      setTimeout(() => {
        setShowFeedbackForm(false);
        setSubmitted(false);
      }, 2000);
    }
  };

  if (!isHydrated || !isDelivered) {
    return null;
  }

  if (submitted) {
    return (
      <div className="bg-success/10 border border-success rounded-lg p-6 text-center">
        <Icon name="CheckCircleIcon" size={48} className="text-success mx-auto mb-3" variant="solid" />
        <h3 className="font-heading text-lg font-semibold text-success mb-2">
          Thank You!
        </h3>
        <p className="text-sm text-foreground">Your feedback has been submitted successfully.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-foreground">
          Rate Your Experience
        </h2>
        {!showFeedbackForm && (
          <button
            onClick={() => setShowFeedbackForm(true)}
            className="text-sm text-primary font-medium hover:underline"
          >
            Skip
          </button>
        )}
      </div>

      {!showFeedbackForm ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-6">
            How was your delivery experience?
          </p>
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => {
                  setRating(star);
                  setShowFeedbackForm(true);
                }}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-smooth hover-lift"
              >
                <Icon
                  name="StarIcon"
                  size={40}
                  variant={(hoveredRating || rating) >= star ? 'solid' : 'outline'}
                  className={(hoveredRating || rating) >= star ? 'text-warning' : 'text-muted-foreground'}
                />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="transition-smooth"
              >
                <Icon
                  name="StarIcon"
                  size={32}
                  variant={rating >= star ? 'solid' : 'outline'}
                  className={rating >= star ? 'text-warning' : 'text-muted-foreground'}
                />
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Share your feedback (optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us about your experience..."
              rows={4}
              className="w-full px-4 py-3 bg-background border border-input rounded-md text-sm focus-ring resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowFeedbackForm(false)}
              className="flex-1 px-4 py-3 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={rating === 0}
              className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Feedback
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPrompt;